import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./check-box.module.scss";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import type { BaseCheckBoxProps } from "@whatslly/types/UI-V2/BaseElements/CheckBoxType";
import globalStyles from "../../../v2/globalStyles";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";

export const CheckBox: React.FC<BaseCheckBoxProps> = observer(
  ({
    paddingInline,
    borderRadius,
    borderColor,
    marginTop,
    textFontSize,
    backgroundColor,
    textFontWeight,
    marginBottom,
    defaultValue,
    className,
    value,
    checked,
    text,
    name,
    textColor,
    padding,
    linkText,
    iconColor,
    icon,
    linkHref,
    checkBoxSize,
    checkBoxTextGap,
    notChackedBackGroundColor,
    linkColor,
    clickAbleLabel,
    TextComponent,
    onChange,
    disableUserSelect,
    checkBoxElementClassName,
    disabled,
    textEllipsis,
    textTooltip,
    textTooltipDelay,
    required,
    labelClassName,
    ...props
  }) => {
    const [checkedState, setCheckedState] = useState<boolean>(checked ? checked : false);
    const inputRef = useRef<HTMLInputElement>(null);
    const tuvisOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
          return;
        }
        setCheckedState(e.target.checked);
        if (onChange) {
          return onChange(e);
        }
      },
      [disabled, onChange]
    );
    clickAbleLabel ??= true;

    const handleOnClick = () => {
      if (!inputRef.current || disabled) {
        return;
      }

      inputRef.current.click();
    };
    useEffect(() => {
      setCheckedState(checked ?? false);
    }, [checked]);

    return (
      <div
        className={classnames(
          styles.checkBoxContainer,
          globalStyles[`p-${padding ?? "none"}`],
          globalStyles[`pi-${paddingInline ?? "small"}`],
          globalStyles[`mb-${marginBottom ?? "none"}`],
          styles[checkBoxSize ?? "default"],
          globalStyles[`mt-${marginTop ?? "none"}`],
          { [globalStyles.disabled]: disabled }
        )}
        style={checkBoxTextGap ? { columnGap: `${checkBoxTextGap}px` } : undefined}
      >
        <input
          type={"checkbox"}
          onChange={tuvisOnChange}
          checked={checkedState}
          ref={inputRef}
          name={name ?? ""}
          data-testid={props["data-testid"] ? `${props["data-testid"]}-input` : undefined}
          className={classnames(styles.checkBoxInput)}
        />
        <div
          className={classnames(
            styles.StyledCheckbox,
            globalStyles[`bg-color-${(!checkedState && notChackedBackGroundColor) ?? ""}`],
            globalStyles[`bc-${checkedState ? (backgroundColor ?? "main-primary") : (borderColor ?? "neutral-secondary")}`],
            globalStyles[`check-box-${checkBoxSize ?? "default"}`],
            globalStyles[`color-${iconColor ?? "main-secondary"}`],
            {
              [globalStyles[`bg-color-${backgroundColor ?? "main-primary"}`]]: checkedState,
              [styles.styledCheckboxCheck]: checkedState,
              [styles["br-"]]: !borderRadius,
            },
            className
          )}
          data-testid={props["data-testid"]}
          onClick={handleOnClick}
        >
          {icon ? (
            <SvgV2
              iconName={icon}
              className={classnames(styles.svgClass, {
                [styles.checkedSvg]: checkedState,
              })}
            />
          ) : (
            <svg
              height="100%"
              width="100%"
              fill="currentColor"
              className={classnames(styles.svgClass, {
                [styles.checkedSvg]: checkedState,
              })}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.01603 0.78996C7.26106 1.04327 7.26106 1.45397 7.01603 1.70729L3.62779 5.20999C3.38276 5.4633 2.98548 5.4633 2.74044 5.20999L0.983581 3.39377C0.738546 3.14046 0.738546 2.72976 0.983581 2.47645C1.22862 2.22313 1.6259 2.22313 1.87093 2.47645L3.18412 3.834L6.12868 0.78996C6.37371 0.536647 6.77099 0.536647 7.01603 0.78996Z"
                fill="currentColor"
              />
            </svg>
          )}
        </div>
        {TextComponent ? (
          <TextComponent />
        ) : (
          (text || linkText) && (
            <Tooltip value={text} disabled={!textTooltip} delay={textTooltipDelay || 1000}>
              <div
                className={classnames(
                  styles.textInputContainer,
                  labelClassName,
                  globalStyles[`fs-${textFontSize ?? "default"}`],
                  globalStyles[`fw-${textFontWeight ?? "default"}`],
                  { [globalStyles["pointer"]]: clickAbleLabel, [styles.textEllipsis]: textEllipsis }
                )}
                onClick={clickAbleLabel ? handleOnClick : undefined}
              >
                <p
                  className={classnames(styles.checkBoxText, globalStyles[`color-${textColor ?? "main-primary"}`], {
                    [styles.disableUserSelect]: disableUserSelect,
                  })}
                >
                  {text}{" "}
                  <a className={classnames(styles.checkBoxLink, globalStyles[`color-${linkColor ?? "brand-tertiary"}`])} href={`${linkHref}`}>
                    {linkText}
                  </a>
                  {required && " *"}
                </p>
              </div>
            </Tooltip>
          )
        )}
      </div>
    );
  }
);
